import { Controller } from "@hotwired/stimulus"

// Live preview for manual sales tax (matches ManualSalesTaxCalculatorService).
export default class extends Controller {
  static targets = ["amount", "rate", "preview", "taxAdjustment", "taxRow", "tax", "subtotal", "total"]
  static values  = { balanceCents: Number, fixedTaxPackages: Object, fullBalanceItems: Array }

  connect() {
    this.update()
  }

  update() {
    if (!this.hasPreviewTarget) return

    const raw = this.amountTarget.value
    const dollars = parseFloat(raw)
    const rateRaw = this.rateTarget.value.trim()
    const rate = rateRaw === "" ? 0 : parseFloat(rateRaw)

    const inclusiveRadio = this.element.querySelector(
      'input[name="manual_sales_tax_inclusive"][value="1"]'
    )
    const inclusive = inclusiveRadio && inclusiveRadio.checked

    const fmt = (cents) => `$${(cents / 100).toFixed(2)}`

    const clearPreview = () => {
      this.previewTarget.hidden = true
      if (this.hasTaxAdjustmentTarget) {
        this.taxAdjustmentTarget.textContent = ""
        this.taxAdjustmentTarget.hidden = true
      }
    }

    const setPreview = ({ taxCents, principalCents, collectedCents, inclusive }) => {
      this.taxTarget.textContent = fmt(taxCents)
      if (this.hasTaxRowTarget) this.taxRowTarget.hidden = taxCents <= 0
      this.subtotalTarget.textContent = fmt(principalCents)
      this.totalTarget.textContent = fmt(collectedCents)
      this.previewTarget.hidden = false
      if (this.hasTaxAdjustmentTarget) {
        const sign = inclusive ? "-" : "+"
        this.taxAdjustmentTarget.textContent = `(${sign}${fmt(taxCents)} tax)`
        this.taxAdjustmentTarget.hidden = taxCents <= 0
      }
    }

    if (isNaN(dollars) || dollars <= 0) {
      clearPreview()
      return
    }

    const totalCents = Math.round(dollars * 100)
    const selectedTarget = this.element.querySelector('input[name="driving_school_student_fee_guid"]:checked')
    if (selectedTarget && selectedTarget.value === "full_balance" && this.hasFullBalanceItemsValue) {
      const taxCents = this.fullBalanceTaxForPrincipal(totalCents)
      setPreview({
        taxCents,
        principalCents: totalCents,
        collectedCents: totalCents + taxCents,
        inclusive: false
      })
      return
    }

    const chargeSalesTax = this.element.querySelector('input[name="charge_manual_sales_tax"]')
    if (chargeSalesTax && !chargeSalesTax.checked) {
      clearPreview()
      return
    }

    const fixedTaxPackage = selectedTarget && this.hasFixedTaxPackagesValue
      ? this.fixedTaxPackagesValue[selectedTarget.value]
      : null
    const balanceCents = fixedTaxPackage
      ? Number(fixedTaxPackage.remainingPrincipalCents || 0)
      : (this.balanceCentsValue || 0)
    const calculationRate = fixedTaxPackage
      ? Number(fixedTaxPackage.effectiveRatePercent || 0)
      : rate

    if (isNaN(calculationRate) || calculationRate <= 0) {
      clearPreview()
      return
    }

    let taxCents, principalCents, collectedCents

    if (inclusive) {
      // Admin enters total cash collected; tax is extracted from within that amount.
      // Only the pre-tax principal is credited against the lesson balance.
      const remainingTaxCents = fixedTaxPackage ? Number(fixedTaxPackage.remainingTaxCents || 0) : null
      const outstandingCents = fixedTaxPackage ? balanceCents + remainingTaxCents : null
      taxCents = fixedTaxPackage && totalCents >= outstandingCents
        ? remainingTaxCents
        : Math.round(totalCents - (totalCents / (1 + calculationRate / 100)))
      if (remainingTaxCents != null) taxCents = Math.min(taxCents, remainingTaxCents)
      if (taxCents < 0) taxCents = 0
      principalCents = totalCents - taxCents
      collectedCents = totalCents

      setPreview({ taxCents, principalCents, collectedCents, inclusive: true })
    } else {
      // Student pays totalCents (pre-tax); tax is added on top and collected separately.
      principalCents = totalCents
      taxCents = fixedTaxPackage && principalCents >= balanceCents
        ? Number(fixedTaxPackage.remainingTaxCents || 0)
        : Math.round((principalCents * calculationRate) / 100)
      if (fixedTaxPackage) taxCents = Math.min(taxCents, Number(fixedTaxPackage.remainingTaxCents || 0))
      if (taxCents < 0) taxCents = 0
      collectedCents = principalCents + taxCents

      setPreview({ taxCents, principalCents, collectedCents, inclusive: false })
    }
  }

  fullBalanceTaxForPrincipal(principalCents) {
    let remaining = principalCents
    let tax = 0
    const items = [...this.fullBalanceItemsValue].sort((a, b) => Number(b.taxable) - Number(a.taxable))

    items.forEach(item => {
      if (remaining <= 0) return
      const itemPrincipal = Number(item.principalCents || 0)
      const applied = Math.min(remaining, itemPrincipal)
      remaining -= applied
      if (!item.taxable || applied <= 0) return

      const remainingTax = Number(item.taxCents || 0)
      const rate = Number(item.effectiveRatePercent || 0)
      const itemTax = applied >= itemPrincipal && remainingTax > 0
        ? remainingTax
        : Math.round(applied * rate / 100)
      tax += remainingTax > 0 ? Math.min(itemTax, remainingTax) : itemTax
    })

    return Math.max(tax, 0)
  }
}
